import type { Schema } from '@dealdesk/api-schema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DateField, FileField, FormErrorSummary, applyServerErrors } from '@/business';
import { Button, Dialog, DialogContent, toast } from '@/design-system';
import { apiErrorMessage, isApiError } from '@/shared/api';
import { addDaysToDateOnly, todayInDubai } from '@/shared/lib/dates';

import { useUploadDocument } from '../api/mutations';
import { documentUploadSchema, emptyDocumentUpload, toAttachRequest, type DocumentUploadValues } from '../forms/documentUpload';

type Doc = Schema<'DealDocument'>;

/**
 * Upload (or a new version of) the evidence for one requirement: the file, its issue date and — for expiring types —
 * its expiry date. Opened from the checklist, the bank query reply and the operational steps.
 */
export function DocumentUploadDialog({ dealId, doc, onClose }: { dealId: string; doc: Doc; onClose: () => void }) {
  const { t } = useTranslation('deals');
  const today = todayInDubai();
  const hasExpiry = doc.hasExpiry ?? false;
  const schema = useMemo(() => documentUploadSchema(today, hasExpiry), [today, hasExpiry]);
  const form = useForm<DocumentUploadValues>({ resolver: zodResolver(schema), defaultValues: emptyDocumentUpload() });
  const upload = useUploadDocument(dealId);
  const [progress, setProgress] = useState<number | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const fileError = form.formState.errors.file?.message;
  const replacing = Boolean(doc.evidence);

  const submit = form.handleSubmit(async (v) => {
    setProblem(null);
    if (!v.file) return;
    setProgress(0);
    try {
      await upload.mutateAsync({ documentId: doc.id, file: v.file, onProgress: setProgress, dates: toAttachRequest(v, hasExpiry) });
      toast.done(t('documents.uploaded', { name: doc.name }));
      onClose();
    } catch (e) {
      if (isApiError(e)) applyServerErrors(form, e, ['issuedOn', 'expiresOn']);
      setProblem(isApiError(e) ? apiErrorMessage(e) : t('errors.uploadFailed', { ns: 'common' }));
    } finally {
      setProgress(null);
    }
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        size="form"
        title={replacing ? t('documents.uploadDialog.replaceTitle', { name: doc.name }) : t('documents.uploadDialog.title', { name: doc.name })}
        description={replacing ? t('documents.uploadDialog.replaceBody') : t('documents.uploadDialog.body')}
        footer={
          <>
            <Button onClick={onClose}>{t('actions.cancel', { ns: 'common' })}</Button>
            <Button variant="primary" loading={upload.isPending} onClick={() => void submit()}>
              {progress !== null ? t('files.uploading', { ns: 'common', percent: progress }) : t('documents.upload')}
            </Button>
          </>
        }
      >
        <form onSubmit={(e) => void submit(e)} noValidate className="flex flex-col gap-4">
          {problem ? <FormErrorSummary title={problem} /> : null}
          <FileField
            label={t('documents.uploadDialog.file')}
            purpose="document"
            error={fileError ? t(fileError as 'form.required', { ns: 'common' }) : undefined}
            onChange={(file) => form.setValue('file', file, { shouldValidate: form.formState.isSubmitted })}
          />
          <div className="grid grid-cols-2 gap-4">
            <DateField control={form.control} name="issuedOn" label={t('documents.uploadDialog.issuedOn')} max={today} optional help={t('documents.uploadDialog.issuedHelp')} />
            {hasExpiry ? <DateField control={form.control} name="expiresOn" label={t('documents.uploadDialog.expiresOn')} min={addDaysToDateOnly(today, 1)} help={t('documents.uploadDialog.expiresHelp')} /> : null}
          </div>
          <button type="submit" hidden />
        </form>
      </DialogContent>
    </Dialog>
  );
}
